import { BASE_URL, localizedUrl, normalizeLocale } from "@/shared/lib/seo"
import { toIsoSeoDate } from "@/shared/lib/seo-date"
import { normalizeTreatmentCaseBodyHeadings, stripTreatmentCaseDocumentShell } from "@/entities/treatment-case/lib/seo"
import type { HealthInfoDetail, HealthInfoListItem } from "../model/types"

export const HEALTH_INFO_DISCLAIMER = "본 콘텐츠는 의학 정보 제공을 목적으로 하며, 특정 의료기관·의료인의 진단·치료를 대체하지 않습니다. 증상이 있는 경우 의료기관에서 진료를 받으시기 바랍니다."

/** 공개 API가 승인한 구조화 관계만 표시한다. 참고문헌 URL 검증과는 별개다. */
export const getHealthInfoRelatedHospital = (
  item: Pick<HealthInfoListItem, "hospitalOriginId" | "hospitalOriginName">,
): { id: number; name: string } | null => {
  const id = item.hospitalOriginId
  const name = typeof item.hospitalOriginName === "string" ? item.hospitalOriginName.trim() : ""
  return typeof id === "number" && Number.isSafeInteger(id) && id > 0 && name
    ? { id, name }
    : null
}

/* 치료사례와 같은 CMS 문서 껍데기·CSS 격리 관례를 그대로 쓴다. sanitizer 가 아니다 —
 * 본문 마크업 차단은 2026-09-18 계약 v1.2 로 폐기됐고 판단은 어드민 검수에 있다.
 *
 * 본문 heading 강등도 치료사례와 같이 한다. 상세 페이지의 H1 은 title 필드가 만들고,
 * 통짜 HTML 문서를 붙여 넣으면 그 문서의 <h1> 이 본문에 남아 H1 이 2개가 된다
 * (reviewed.co.kr/ko/health-info/2 에서 실제로 2개였다). 치료사례는
 * toTreatmentCaseBodyHtml 에서 이미 강등하는데 건강정보만 그 단계가 빠져 있었다. */
export const toHealthInfoBodyHtml = (html = "") =>
  normalizeTreatmentCaseBodyHeadings(stripTreatmentCaseDocumentShell(html))

const date = (value?: string | null) => {
  const normalized = value?.trim()
  if (!normalized) return undefined
  const day = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/)
  if (!day) return undefined
  const checked = new Date(`${day[1]}-${day[2]}-${day[3]}T00:00:00Z`)
  if (Number.isNaN(checked.getTime()) || checked.toISOString().slice(0, 10) !== day[0].slice(0, 10)) return undefined
  return toIsoSeoDate(normalized)
}

const sourceUrl = (value?: string | null) => {
  try {
    const url = new URL(value?.trim() ?? "")
    const internal = url.hostname === "reviewed.co.kr" || url.hostname.endsWith(".reviewed.co.kr")
    const decodedPath = decodeURIComponent(url.pathname)
    if (internal && /^\/(?:[a-z]{2}\/)?(?:hospitals?|search|rankings?|doctors?)(?:\/|$)/i.test(decodedPath)) return undefined
    return ["https:", "http:"].includes(url.protocol) ? url.href : undefined
  } catch {
    return undefined
  }
}

/** One normalized source for visible attribution and structured data. */
export const getHealthInfoAttribution = (detail: HealthInfoDetail) => {
  const reviewerName = detail.reviewerName?.trim() || undefined
  return {
    authorName: detail.authorName.trim(),
    reviewerName,
    reviewerCredential: reviewerName ? detail.reviewerCredential?.trim() || undefined : undefined,
    reviewedAt: reviewerName ? date(detail.reviewedAt) : undefined,
    publishedAt: date(detail.publishedAt),
    updatedAt: date(detail.updatedAt),
    sources: (detail.sources ?? []).map(source => ({
      organization: source.organization.trim(),
      sourceTitle: source.sourceTitle.trim(),
      sourceUrl: sourceUrl(source.sourceUrl),
      sortOrder: source.sortOrder,
    })).filter(source => source.organization && source.sourceTitle)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  }
}

export const buildHealthInfoSchema = ({ locale, detail }: { locale: string; detail: HealthInfoDetail }) => {
  const attribution = getHealthInfoAttribution(detail)
  const url = localizedUrl(`health-info/${detail.id}`, normalizeLocale(locale), BASE_URL)
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "@id": `${url}#health-info`,
    url,
    headline: detail.title,
    about: { "@type": "MedicalCondition", name: detail.title },
    ...(attribution.reviewerName ? { reviewedBy: {
      "@type": "Person", name: attribution.reviewerName,
      ...(attribution.reviewerCredential ? { jobTitle: attribution.reviewerCredential } : {}),
    } } : {}),
    ...(attribution.reviewedAt ? { lastReviewed: attribution.reviewedAt } : {}),
    ...(attribution.publishedAt ? { datePublished: attribution.publishedAt } : {}),
    ...(attribution.updatedAt ? { dateModified: attribution.updatedAt } : {}),
    ...(attribution.sources.length ? { citation: attribution.sources.map(source => ({
      "@type": "CreativeWork", name: source.sourceTitle,
      publisher: { "@type": "Organization", name: source.organization },
      ...(source.sourceUrl ? { url: source.sourceUrl } : {}),
    })) } : {}),
    publisher: { "@id": `${BASE_URL}/#organization` },
  }
}
